import React, { useState } from "react";
import useAdminPartner from "../hooks/useAdminPartner";
import {
  Button,
  FormControl,
  Grid,
  Paper,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import useAPI from "../hooks/useAPI";
import { partnersActions } from "../state/partners";
import { useDispatch } from "react-redux";
import UploadImage from "./UploadImage";
import { Storage } from "aws-amplify";
import shortid from "shortid";

export default function AdminPartner(props) {
  const {
    currentItem,
    onSave,
    callback,
    canEdit = true,
    onCancelAction,
  } = props;
  useAdminPartner(props);
  const dispatch = useDispatch();

  const [id, setId] = useState(currentItem?.id || 0);
  const [name, setName] = useState(currentItem?.name || "");
  const [description, setDescription] = useState(
    currentItem?.description || ""
  );
  const [location, setLocation] = useState(currentItem?.location || "");
  const [website, setWebsite] = useState(currentItem?.website || "");
  const [instagram, setInstagram] = useState(currentItem?.instagram || "");
  const [facebook, setFacebook] = useState(currentItem?.facebook || "");
  const [tiktok, setTiktok] = useState(currentItem?.tiktok || "");
  const [whatsapp, setWhatsapp] = useState(currentItem?.whatsapp || "");
  const [photos, setPhotos] = useState(currentItem?.photos || []);
  const [affiliatedItems, setAffiliatedItems] = useState(
    currentItem?.metadata?.affiliatedItems || []
  );
  const [removedItems, setRemovedItems] = useState([]);
  const [openAffiliationDialog, setOpenAffiliationDialog] = useState(false);

  // Mettre à jour les états quand currentItem change
  React.useEffect(() => {
    if (currentItem) {
      setId(currentItem.id || 0);
      setName(currentItem.name || "");
      setDescription(currentItem.description || "");
      setLocation(currentItem.location || "");
      setWebsite(currentItem.website || "");
      setInstagram(currentItem.instagram || "");
      setFacebook(currentItem.facebook || "");
      setTiktok(currentItem.tiktok || "");
      setWhatsapp(currentItem.whatsapp || "");
      setPhotos(currentItem.photos || []);
      setAffiliatedItems(currentItem.metadata?.affiliatedItems || []);
      setRemovedItems([]);
    } else {
      // Réinitialiser pour un nouveau partenaire
      setId(0);
      setName("");
      setDescription("");
      setLocation("");
      setWebsite("");
      setInstagram("");
      setFacebook("");
      setTiktok("");
      setWhatsapp("");
      setPhotos([]);
    }
  }, [currentItem]);

  const { data: partners, query: fetchPartners } = useAPI({
    type: "get",
    url: `partners`,
    defer: true,
  });

  const { query: postItem } = useAPI({
    type: "post",
    url: `partner`,
    defer: true,
  });

  const { query: updateItem } = useAPI({
    type: "put",
    url: `partner/${id}`,
    defer: true,
  });

  const {
    data: itemsData,
    query: fetchItems,
    loading: itemsLoading,
  } = useAPI({
    type: "get",
    url: `items`,
    defer: true,
  });

  React.useEffect(() => {
    if (partners && partners.data) {
      dispatch(partnersActions.fetchAll(partners.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partners]);

  const canSave = name.length === 0;

  const resetForm = () => {
    setId(0);
    setName("");
    setDescription("");
    setLocation("");
    setWebsite("");
    setInstagram("");
    setFacebook("");
    setTiktok("");
    setWhatsapp("");
    setPhotos([]);
    setAffiliatedItems([]);
    setRemovedItems([]);
  };

  const handleSavePartner = async () => {
    try {
      const bodyData = {
        name,
        description,
        location,
        website,
        instagram,
        facebook,
        tiktok,
        whatsapp,
        photos,
        metadata: {
          affiliatedItems,
        },
      };

      console.log("Saving partner with data:", { id, ...bodyData });

      if (id && id > 0) {
        // Mise à jour : PUT /partner/:id avec le nouveau format
        await updateItem({
          url: `partner/${id}`,
          body: {
            partnerUpdated: bodyData,
            removed: removedItems,
          },
        });
      } else {
        // Création : POST /partner
        await postItem({
          body: bodyData,
        });
      }

      await fetchPartners();
      resetForm();
      onSave({ success: true, message: "Partenaire sauvegardé avec succès" });
      callback?.();
    } catch (err) {
      console.error("Erreur lors de la sauvegarde du partenaire:", err);
      alert("Erreur lors de la sauvegarde du partenaire.");
    }
  };

  const handlePhotoUpload = async (e) => {
    try {
      for (let i = 0; i < e.target.files.length; i++) {
        const file = e.target.files[i];
        const filename = `kadomi/partners/${shortid.generate()}-${file.name}`;

        await Storage.put(filename, file, {
          contentType: file.type,
          progressCallback(progress) {
            const progressPercent = (progress.loaded / progress.total) * 100;
            console.log(`Uploaded: ${Math.round(progressPercent)}%`);
          },
        });

        setPhotos((prev) => [...prev, filename]);
      }
    } catch (error) {
      console.error("Authentication or upload error:", error);
    }
  };

  const handleRemovePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  return (
    <React.Fragment>
      <Grid container spacing={3}>
        <Grid md={6} item xs={12}>
          <Paper elevation={3} style={{ padding: 20, margin: 20 }}>
            <h2>{id > 0 && `#${id} - `}Informations du Partenaire</h2>
            <p>
              Veuillez remplir les informations ci-dessous pour créer ou
              modifier un partenaire.
            </p>

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <TextField
                id="partner-name"
                label="Nom *"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Entrez le nom du partenaire"
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <TextField
                id="partner-description"
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Entrez une description du partenaire"
                multiline
                rows={4}
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <TextField
                id="partner-location"
                label="Lieu"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Entrez le lieu du partenaire"
              />
            </FormControl>

            <hr style={{ margin: "20px 0" }} />

            <Typography variant="h6" gutterBottom>
              Réseaux sociaux et contact
            </Typography>

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <TextField
                id="partner-website"
                label="Site Web"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://www.exemple.com"
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <TextField
                id="partner-instagram"
                label="Instagram"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder="@nom_instagram"
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <TextField
                id="partner-facebook"
                label="Facebook"
                value={facebook}
                onChange={(e) => setFacebook(e.target.value)}
                placeholder="https://www.facebook.com/page"
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <TextField
                id="partner-tiktok"
                label="TikTok"
                value={tiktok}
                onChange={(e) => setTiktok(e.target.value)}
                placeholder="@nom_tiktok"
              />
            </FormControl>

            <FormControl fullWidth style={{ marginBottom: 20 }}>
              <TextField
                id="partner-whatsapp"
                label="WhatsApp"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="+41 xx xxx xx xx"
              />
            </FormControl>
          </Paper>
        </Grid>

        <Grid md={6} item xs={12}>
          <Paper elevation={3} style={{ padding: 20, margin: 20 }}>
            <h2>Photos</h2>
            <p>
              Ajoutez des photos pour illustrer le partenaire. Vous pouvez
              ajouter plusieurs photos.
            </p>

            <div style={{ marginBottom: 20 }}>
              <UploadImage onFileChange={handlePhotoUpload} />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                gap: "10px",
                marginTop: 20,
              }}
            >
              {photos.map((photo, index) => (
                <div
                  key={index}
                  style={{
                    position: "relative",
                    paddingBottom: "100%",
                    overflow: "hidden",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                  }}
                >
                  <img
                    src={`${process.env.REACT_APP_BUCKETS3}${photo}`}
                    alt={`Partenaire ${index + 1}`}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => handleRemovePhoto(index)}
                    style={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      minWidth: 30,
                      padding: "4px 8px",
                    }}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>

            {photos.length === 0 && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ textAlign: "center", marginTop: 20 }}
              >
                Aucune photo ajoutée pour le moment
              </Typography>
            )}
          </Paper>

          <Paper elevation={3} style={{ padding: 20, margin: 20 }}>
            <h2>Informations</h2>
            <p>
              Cette interface permet de gérer les partenaires de votre
              plateforme. Un partenaire peut avoir plusieurs liens vers ses
              réseaux sociaux et plusieurs photos.
            </p>
            <p>
              Assurez-vous de remplir au moins le nom du partenaire. Les autres
              informations sont optionnelles mais recommandées pour une
              meilleure présentation.
            </p>
          </Paper>
        </Grid>
      </Grid>

      <div style={{ height: 50 }}>
        <Button
          variant="contained"
          disabled={canSave}
          onClick={handleSavePartner}
        >
          {id ? "Modifier" : "Créer"}
        </Button>
        {id && id > 0 && (
          <Button
            variant="outlined"
            color="primary"
            style={{ marginLeft: 10 }}
            onClick={() => {
              setOpenAffiliationDialog(true);
              fetchItems();
            }}
          >
            Affiliation ({affiliatedItems.length})
          </Button>
        )}
        <Button
          variant="contained"
          style={{ marginLeft: 10 }}
          onClick={resetForm}
        >
          Réinitialiser
        </Button>
        <Button
          style={{ marginLeft: 10 }}
          variant="contained"
          onClick={onCancelAction}
        >
          Annuler
        </Button>
      </div>

      {/* Dialog d'affiliation */}
      <Dialog
        open={openAffiliationDialog}
        onClose={() => setOpenAffiliationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Affiliation des articles
          <Typography variant="body2" color="textSecondary">
            Sélectionnez les articles affiliés à ce partenaire
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {itemsLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <CircularProgress />
            </div>
          ) : (
            <List>
              {itemsData?.items?.map((item) => {
                const isAffiliated = affiliatedItems.some(
                  (affItem) => affItem.id === item.id
                );
                return (
                  <ListItem
                    key={item.id}
                    button
                    onClick={() => {
                      if (isAffiliated) {
                        // Ajouter à la liste des supprimés si c'était déjà affilié
                        const removedItem = affiliatedItems.find(
                          (affItem) => affItem.id === item.id
                        );
                        if (removedItem) {
                          setRemovedItems([...removedItems, removedItem]);
                        }
                        setAffiliatedItems(
                          affiliatedItems.filter(
                            (affItem) => affItem.id !== item.id
                          )
                        );
                      } else {
                        // Si on re-sélectionne un item qui était dans removed, le retirer de removed
                        setRemovedItems(
                          removedItems.filter((rItem) => rItem.id !== item.id)
                        );
                        setAffiliatedItems([
                          ...affiliatedItems,
                          { id: item.id, title: item.title },
                        ]);
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={isAffiliated}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${item.id}# ${item.title}`}
                      secondary={
                        <>
                          {item.location && `📍 ${item.location}`}
                          {item.prices && item.prices.length > 0 && (
                            <span style={{ marginLeft: 10 }}>
                              💰 {item.prices[0].price}{" "}
                              {item.prices[0].currency}
                            </span>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
              {itemsData?.items?.length === 0 && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ textAlign: "center", padding: 20 }}
                >
                  Aucun article disponible
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAffiliationDialog(false)}>
            Annuler
          </Button>
          <Button
            onClick={() => {
              setOpenAffiliationDialog(false);
            }}
            variant="contained"
            color="primary"
          >
            Valider ({affiliatedItems.length} sélectionné
            {affiliatedItems.length > 1 ? "s" : ""})
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
